import { useState } from "react";

import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/ui/tooltip";
import { H2, P } from "~/components/ui/typography";
import type { TPartInteractionWithImage } from "~/database/types";
import { useCanDisplayPartImages } from "~/hooks/use-can-display-part-images";
import { useSession } from "~/hooks/use-session";
import type { IdentifiedPart } from "~/types/global";

import { PartsList } from "./parts-list";

// AIDEV-NOTE: Session Notes — small name-only "pill" chips for parts that don't
// warrant a full entry. Two variants with distinct styling + hover copy:
//  - "mentioned": named in-session but too thin for a full reflection (solid border).
//  - "suspected": inferred from indirect signals, not directly explored (dashed/muted).
const stripPartSuffix = (name: string) =>
    name.endsWith(" Part") ? name.replace(" Part", "") : name;

const PartPill = ({
    name,
    variant,
    suffix,
}: {
    name: string;
    variant: "mentioned" | "suspected";
    suffix?: React.ReactNode;
}) => {
    // AIDEV-NOTE: Hover tooltip is desktop-only; on touch devices the pill is
    // tappable and opens a small modal explaining the thin coverage.
    const [open, setOpen] = useState(false);
    const tooltip =
        variant === "mentioned"
            ? "You named this part, but it didn't come up enough this session to write a full reflection on it."
            : "We noticed hints of this part, though it wasn't explored directly — it may be worth turning toward.";

    return (
        <>
            <Tooltip>
                <TooltipTrigger asChild>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className={
                            variant === "mentioned"
                                ? "inline-flex cursor-pointer items-center rounded-[20px] border border-brand-forest-200 bg-white px-3.5 py-1.5 text-sm text-brand-forest-700"
                                : "inline-flex cursor-pointer items-center rounded-[20px] border border-dashed border-brand-forest-200 bg-brand-forest-50/50 px-3.5 py-1.5 text-sm text-brand-forest-500"
                        }
                    >
                        {stripPartSuffix(name)}
                        {suffix}
                    </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                    <p className="max-w-[220px]">{tooltip}</p>
                </TooltipContent>
            </Tooltip>
            <Dialog
                analyticsId="SessionPartPillDetail"
                open={open}
                onOpenChange={setOpen}
            >
                <DialogContent className="max-w-xs">
                    <DialogTitle className="sr-only">Part detail</DialogTitle>
                    <p className="pr-6 text-sm text-brand-forest-700">
                        This part was detected but there is not enough
                        information in the session to display full details.
                    </p>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const SessionParts = ({
    sessionId,
    parts,
    handlePartClick,
    partSimilarities,
    isPractitioner = false,
    meditationsEnabledForClient,
    forceShowClientFeatures = false,
}: {
    sessionId: string;
    // AIDEV-NOTE: sessionPipelineV2 adds an optional `suspectedParts[]` light
    // bucket for SYNTHESIZED parts inferred from structural evidence (polarity,
    // somatic, narrative_gap, …) without a Pass 1 anchor. We fold them into
    // `lowConfidenceSuspected` so the existing "Potential unaddressed parts"
    // UI + `hideSuspectedParts` setting picks them up without further wiring.
    parts: {
        present: TPartInteractionWithImage[];
        other: IdentifiedPart[];
        suspectedParts?: IdentifiedPart[];
    };
    handlePartClick: (partInteraction: TPartInteractionWithImage) => void;
    // AIDEV-NOTE: confidence accepts categorical ("high"/"medium"/"low") or legacy numeric
    partSimilarities?: Record<
        string,
        Array<{
            partId: string;
            confidence: "high" | "medium" | "low" | number;
            reason: string;
        }>
    >;
    isPractitioner?: boolean;
    /** The session OWNER's meditation preference — not the viewer's. */
    meditationsEnabledForClient?: boolean;
    /** Keeps the real demo content mounted while onboarding explains visibility. */
    forceShowClientFeatures?: boolean;
}) => {
    const { effectiveSettings } = useSession(sessionId);
    const { canDisplay: canDisplayPartImages } =
        useCanDisplayPartImages(sessionId);

    const focalParts = parts.present.filter(
        (part) => part.presenceLevel?.toLowerCase().includes("focal"),
    );

    const mentionedParts = parts.present.filter(
        (part) => part.presenceLevel?.toLowerCase().includes("mentioned"),
    );

    const suspectedParts = parts.present.filter(
        (part) =>
            part.presenceLevel?.toLowerCase().includes("suspected") &&
            part.evidenceLevel?.toLowerCase().includes("high"),
    );

    const brieflyMentionedParts = parts.other.filter((part) =>
        part.presenceLevel.toLowerCase().includes("mentioned"),
    );

    const lowConfidenceSuspected = [
        ...parts.other.filter(
            (part) =>
                part.presenceLevel.toLowerCase().includes("suspected") &&
                !part.evidenceLevel.toLowerCase().includes("high"),
        ),
        // AIDEV-NOTE: v2 synthesized-suspected bucket. These never have a
        // Pass 1 anchor so they always land here (the v2 prompt forces
        // presenceLevel='suspected' on every entry in this bucket).
        ...(parts.suspectedParts ?? []),
    ];

    const hasAnyParts =
        focalParts.length > 0 ||
        mentionedParts.length > 0 ||
        brieflyMentionedParts.length > 0 ||
        suspectedParts.length > 0 ||
        lowConfidenceSuspected.length > 0;

    return (
        <TooltipProvider>
            <div className="mt-4 space-y-12">
                {!hasAnyParts ? (
                    <div>
                        <P>None this session.</P>
                    </div>
                ) : (
                    focalParts.length > 0 && (
                        <div>
                            {/* Note: we don't show the title here because it's already shown in the right column / session page */}
                            <PartsList
                                partInteractions={focalParts}
                                onPartClick={handlePartClick}
                                meditationsEnabledForClient={
                                    meditationsEnabledForClient
                                }
                                showPartImages={
                                    forceShowClientFeatures ||
                                    canDisplayPartImages
                                }
                                partSimilarities={partSimilarities}
                                isPractitioner={isPractitioner}
                            />
                        </div>
                    )
                )}
                {(mentionedParts.length > 0 ||
                    brieflyMentionedParts.length > 0) && (
                    <div>
                        <H2>Parts you mentioned</H2>
                        <PartsList
                            partInteractions={mentionedParts}
                            onPartClick={handlePartClick}
                            meditationsEnabledForClient={
                                meditationsEnabledForClient
                            }
                            showPartImages={
                                forceShowClientFeatures || canDisplayPartImages
                            }
                            partSimilarities={partSimilarities}
                            isPractitioner={isPractitioner}
                        />
                        {/* AIDEV-NOTE: Session Notes redesign (mockup 2) — briefly-mentioned
                        parts (no full entry) render as quiet name-only pill chips
                        with a hover tooltip explaining the thin coverage. */}
                        {brieflyMentionedParts.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2">
                                {brieflyMentionedParts.map((part) => {
                                    const isLow = part.evidenceLevel
                                        .toLowerCase()
                                        .includes("low");
                                    return (
                                        <PartPill
                                            key={part.name}
                                            name={part.name}
                                            variant="mentioned"
                                            suffix={
                                                isLow ? (
                                                    <span className="ml-1 text-brand-forest-400">
                                                        (Low confidence)
                                                    </span>
                                                ) : undefined
                                            }
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {(forceShowClientFeatures ||
                    !effectiveSettings?.hideSuspectedParts) &&
                    (suspectedParts.length > 0 ||
                        lowConfidenceSuspected.length > 0) && (
                        <div data-tour-id="suspected-parts">
                            <H2>Potential unaddressed parts</H2>
                            <PartsList
                                partInteractions={suspectedParts}
                                onPartClick={handlePartClick}
                                showPartImages={
                                    forceShowClientFeatures ||
                                    canDisplayPartImages
                                }
                                meditationsEnabledForClient={
                                    meditationsEnabledForClient
                                }
                                partSimilarities={partSimilarities}
                                isPractitioner={isPractitioner}
                            />
                            {/* AIDEV-NOTE: lower-confidence suspected parts render as
                            dashed/muted pills (distinct from solid "mentioned" pills)
                            with their own hover copy framing them as inferred. */}
                            {lowConfidenceSuspected.length > 0 && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {lowConfidenceSuspected.map((part) => (
                                        <PartPill
                                            key={part.name}
                                            name={part.name}
                                            variant="suspected"
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
            </div>
        </TooltipProvider>
    );
};
